"use client";

import { zodResolver } from "@hookform/resolvers/zod";
import { useTranslations } from "next-intl";
import Link from "next/link";
import { useMemo, useState, useTransition } from "react";
import { flushSync } from "react-dom";
import { useForm } from "react-hook-form";
import { z } from "zod";

import { HR, Large } from "~/components/typography";
import { Button, buttonVariants } from "~/components/ui/button";
import { Input } from "~/components/ui/input";
import { useTrack } from "~/lib/use-track";
import { useRouter } from "~/navigation";
import { trpcReact } from "~/trpc/client";

export function Verify({
  googleAuthUrl,
  onSuccess,
}: {
  googleAuthUrl: string;
  onSuccess?: () => void;
}) {
  const t = useTranslations("Checkout");
  const tCommon = useTranslations("Common");

  const emailSchema = useMemo(() => {
    return z.object({
      email: z.string().email(t("emailIncorrectlyFormatted")),
    });
  }, [t]);

  const otpSchema = useMemo(() => {
    return z.object({
      code: z
        .string()
        .min(6, t("codeMustBe6Digits"))
        .max(6, t("codeMustBe6Digits")),
    });
  }, [t]);

  type EmailFormData = z.infer<typeof emailSchema>;
  type OtpFormData = z.infer<typeof otpSchema>;

  const router = useRouter();
  const track = useTrack();
  const [page, setPage] = useState<"email" | "otp">("email");

  const requestOtpCode = trpcReact.auth.requestOtpCode.useMutation();
  const verifyOtpCode = trpcReact.auth.verifyOtpCode.useMutation({
    onSuccess: () => {
      if (onSuccess) {
        onSuccess();
      }
    },
  });
  const [isPending, startTransition] = useTransition();

  const emailForm = useForm({
    resolver: zodResolver(emailSchema),
    defaultValues: {
      email: "",
    },
  });

  const otpForm = useForm({
    resolver: zodResolver(otpSchema),
    defaultValues: {
      code: "",
    },
  });

  const onSubmitEmail = async (data: EmailFormData) => {
    track({ name: "Start Verify Email" });
    await requestOtpCode.mutateAsync(data);
    setPage("otp");
  };

  const onSubmitOtp = async (data: OtpFormData) => {
    track({ name: "Attempt Verify Code" });
    const response = await verifyOtpCode.mutateAsync({
      code: data.code,
      email: emailForm.getValues("email"),
    });
    if (response.email) {
      startTransition(() => {
        track({ name: "Complete Verification" });
        router.refresh();
      });
    } else {
      otpForm.setError("code", { message: t("invalidCode") });
    }
  };

  const isLoading =
    requestOtpCode.isPending || verifyOtpCode.isPending || isPending;

  if (page === "email") {
    return (
      <form
        onSubmit={emailForm.handleSubmit(onSubmitEmail)}
        className="shadow-card-sm my-8 space-y-4 rounded-xl bg-white/50 p-8"
      >
        <Large className="block whitespace-nowrap">
          {t("verifyEmailAddress")}
        </Large>
        <Input
          disabled={isLoading}
          placeholder={t("emailAddress")}
          type="email"
          {...emailForm.register("email")}
        />
        {emailForm.formState.errors.email ? (
          <div className="text-japan text-sm font-medium">
            {emailForm.formState.errors.email.message}
          </div>
        ) : null}
        <Button variant="sunset" disabled={isLoading} className="w-full">
          {t("verify")}
        </Button>
        <div className="my-4 grid grid-cols-[1fr_auto_1fr] items-center gap-4">
          <HR />
          <span className="text-sm text-neutral-500">or</span>
          <HR />
        </div>
        <Link
          href={googleAuthUrl}
          className={buttonVariants({ className: "w-full" })}
        >
          <div className="relative mr-3 size-5">
            <svg
              className="absolute inset-0 size-full"
              viewBox="0 0 48 48"
              xmlns="http://www.w3.org/2000/svg"
              aria-hidden="true"
              focusable="false"
            >
              <path
                fill="#EA4335"
                d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"
              ></path>
              <path
                fill="#4285F4"
                d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"
              ></path>
              <path
                fill="#FBBC05"
                d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"
              ></path>
              <path
                fill="#34A853"
                d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"
              ></path>
              <path fill="none" d="M0 0h48v48H0z"></path>
            </svg>
          </div>
          <span className="font-medium">{t("continueWithGoogle")}</span>
        </Link>
      </form>
    );
  } else {
    return (
      <form
        onSubmit={otpForm.handleSubmit(onSubmitOtp)}
        className="shadow-card-sm space-y-4 rounded-xl bg-white/50 p-8"
      >
        <Large className="block whitespace-nowrap">
          {t("verificationCode")}
        </Large>
        <Input
          disabled={isLoading}
          placeholder="・・・・・・"
          autoComplete="one-time-code"
          {...otpForm.register("code", {
            onChange: (event: React.ChangeEvent<HTMLInputElement>) => {
              const value = event.target.value;
              // eslint-disable-next-line @eslint-react/dom/no-flush-sync
              flushSync(() => {
                if (!otpForm.formState.isSubmitting && /^\d{6}$/.test(value)) {
                  setTimeout(() => {
                    void otpForm.handleSubmit(onSubmitOtp)();
                  }, 0);
                }
              });
            },
          })}
        />
        {otpForm.formState.errors.code ? (
          <div className="text-japan text-sm font-medium">
            {otpForm.formState.errors.code.message}
          </div>
        ) : null}
        <Button variant="sunset" className="w-full" disabled={isLoading}>
          {tCommon("submit")}
        </Button>
      </form>
    );
  }
}
